import { useQuery, useQueries } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions } from '@frontend/react-query-helpers';
import { SchemaTenCoordinatorV1Service } from '../service';
import { GetTCRBrandLockStatusIO } from '../types';

export const getGetTCRBrandLockStatusQueryKey = (req: GetTCRBrandLockStatusIO['input']) =>
  ['TenCoordinatorV1', 'GetTCRBrandLockStatus', req] as const;
type QueryKey = ReturnType<typeof getGetTCRBrandLockStatusQueryKey>;

/**
 * A hook that returns a query for the `GetTCRBrandLockStatus` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetTCRBrandLockStatusQuery = <E = unknown, D = GetTCRBrandLockStatusIO['output']>(
  req: GetTCRBrandLockStatusIO['input'],
  options?: LimitedSchemaQueryOptions<GetTCRBrandLockStatusIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetTCRBrandLockStatusIO['output'], E, D, QueryKey>({
    queryKey: getGetTCRBrandLockStatusQueryKey(req),
    queryFn: () => SchemaTenCoordinatorV1Service.GetTCRBrandLockStatus(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetTCRBrandLockStatus` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetTCRBrandLockStatusQueries = <E = unknown, D = GetTCRBrandLockStatusIO['output']>(
  argsArr: {
    req: GetTCRBrandLockStatusIO['input'];
    options?: LimitedSchemaQueryOptions<GetTCRBrandLockStatusIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetTCRBrandLockStatusQueryKey(req),
      queryFn: () => SchemaTenCoordinatorV1Service.GetTCRBrandLockStatus(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetTCRBrandLockStatusQuery<E, D>>[];
